import * as React from 'react';
import {
    Route,
    Navigate,
    useLocation,
    RouterProvider,
    createBrowserRouter,
    createRoutesFromElements,
} from 'react-router-dom';
import './App.css';
import AuthProvider from './Authentication/components/AuthProvider';
import Login from './Authentication/components/Login';
import PersistLogin from './Authentication/components/PersistLogin';
import RequireAuth from './Authentication/components/RequireAuth';
import { useEffect } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import PublicFeatureFlags from './Features/components/PublicFeatureFlags';
import PageNotFound from './Common/components/PageNotFound';
import Loading from './Common/components/Loading';
import DeviceRegistration from './Registration/components/DeviceRegistration';
import DeviceList from './Registration/components/DeviceList';
import {
    ApplicationLayout,
    NoLayout,
} from './Common/components/ApplicationLayout';
import EventsList from './Events/components/EventsList';
import CreateEvent from './Events/components/CreateEvent';
import EventDashboard from './Events/components/EventDashboard';
import EventDashboardDetails from './Events/components/EventDashboardDetails';
import EditExperience from './Events/components/EditExperience';
import EventDashboardSharing from './Events/components/EventDashboardSharing';
import CreateAccount from './Authentication/components/CreateAccount';
import CreateFirstEvent from './Onboarding/components/CreateFirstEvent';
import Welcome from './Onboarding/components/Welcome';
import DownloadClickerbooth from './Onboarding/components/DownloadClickerbooth';
import DownloadRomr from './Onboarding/components/DownloadRomr';
import Account from './Account/components/Account';
import Plans from './Plans/components/Plans';
import PasswordResetRequest from './Authentication/components/PasswordResetRequest';
import PasswordReset from './Authentication/components/PasswordReset';
import { AnnouncementsView } from './Announcements';
import Settings from './Settings/components/Settings';
import UsageDashboard from './Usage/components/UsageDashboard';
import Roadmap from './Roadmap/components/Roadmap';
import FeatureRequest from './Roadmap/components/FeatureRequest';

const ROLES = {
    Admin: 0,
    User: 1,
    GlobalAdmin: 69,
};

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route element={<PersistLogin />}>
                <Route
                    element={
                        <RequireAuth
                            allowedRoles={[
                                ROLES.Admin,
                                ROLES.User,
                                ROLES.GlobalAdmin,
                            ]}
                        />
                    }
                >
                    <Route path="/" element={<ApplicationLayout />}>
                        <Route path="/" element={<EventsList />} />
                        <Route
                            path="/announcements"
                            element={<AnnouncementsView />}
                        />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/usage" element={<UsageDashboard />} />
                        <Route path="/devices">
                            <Route index element={<DeviceList />} />
                        </Route>
                        <Route path="/registration">
                            <Route index element={<DeviceRegistration />} />
                            <Route
                                path=":code"
                                element={<DeviceRegistration />}
                            />
                        </Route>
                        <Route path="/events">
                            <Route index element={<EventsList />} />
                            <Route path="create" element={<CreateEvent />} />
                            <Route
                                path=":activationId"
                                element={<EventDashboard />}
                            />
                            <Route
                                path=":activationId/details"
                                element={<EventDashboardDetails />}
                            />
                            <Route
                                path=":activationId/experience"
                                element={<EditExperience />}
                            />
                            <Route
                                path=":activationId/sharing"
                                element={<EventDashboardSharing />}
                            />
                        </Route>
                        <Route path="/plans" element={<Plans />} />
                        <Route path="/account" element={<Account />} />
                        <Route path="/roadmap" element={<Roadmap />} />
                        <Route
                            path="/roadmap/feature-request"
                            element={<FeatureRequest />}
                        />
                    </Route>
                    <Route element={<NoLayout />}>
                        <Route
                            path="/create-first-event"
                            element={<CreateFirstEvent />}
                        />
                        <Route path="/welcome" element={<Welcome />} />
                        <Route
                            path="/download/clickerbooth"
                            element={<DownloadClickerbooth />}
                        />
                        <Route
                            path="/download/romr"
                            element={<DownloadRomr />}
                        />
                    </Route>
                </Route>
                <Route path="/" element={<Navigate to="/home" />} />
                <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route element={<PublicFeatureFlags />}>
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<CreateAccount />} />
            </Route>
            <Route path="/forgot-password" element={<PasswordResetRequest />} />
            <Route path="/password-reset/:token" element={<PasswordReset />} />
        </>,
    ),
);

export default function App() {
    return (
        <AuthProvider>
            <RouterProvider
                router={router}
                fallbackElement={<Loading isLoading={true} />}
            />
            <div className="App"></div>
        </AuthProvider>
    );
}

// eslint-disable-next-line
function FirebaseAnalytics() {
    const location = useLocation();
    useEffect(() => {
        const page_path = location.pathname + location.search;
        const analytics = getAnalytics();
        logEvent(analytics, 'page_view', { firebase_screen: page_path });
    }, [location]);
    return null;
}
